import { hasOriginalText } from "./eventGroupTypes.js";

export const DEFAULT_SYNC_PROPERTIES = {
    timing: true,
    sourceText: true,
    translationText: false,
    position: true,
    speakers: true,
    style: true,
    notes: false,
    reply: false,
    tags: false,
    recipients: false,
};

export const SYNC_PROPERTY_KEYS = Object.keys(DEFAULT_SYNC_PROPERTIES);

/**
 * @param {object} group
 * @returns {boolean}
 */
export function isTranslationGroup(group) {
    return hasOriginalText(group?.type);
}

/**
 * @param {object} eventA
 * @param {object} eventB
 * @returns {number}
 */
export function overlapDuration(eventA, eventB) {
    const startA = eventA?.start;
    const endA = eventA?.end;
    const startB = eventB?.start;
    const endB = eventB?.end;

    if (
        startA === false ||
        endA === false ||
        startB === false ||
        endB === false ||
        startA == null ||
        endA == null ||
        startB == null ||
        endB == null
    ) {
        return 0;
    }

    return Math.max(0, Math.min(endA, endB) - Math.max(startA, startB));
}

/**
 * @param {unknown} value
 * @returns {unknown}
 */
function cloneValue(value) {
    if (Array.isArray(value)) {
        return JSON.parse(JSON.stringify(value));
    }
    return value;
}

/**
 * Map source-language text between groups.
 * @param {object} fromEvent
 * @param {object} toEvent
 * @param {object} fromGroup
 * @param {object} toGroup
 */
function applySourceText(fromEvent, toEvent, fromGroup, toGroup) {
    const fromIsTranslation = isTranslationGroup(fromGroup);
    const toIsTranslation = isTranslationGroup(toGroup);

    if (!fromIsTranslation && toIsTranslation) {
        toEvent.original = fromEvent.text;
        return;
    }

    if (fromIsTranslation && !toIsTranslation) {
        toEvent.text = fromEvent.original;
        return;
    }

    if (fromIsTranslation && toIsTranslation) {
        toEvent.original = fromEvent.original;
        return;
    }

    toEvent.text = fromEvent.text;
}

/**
 * @param {object} fromEvent
 * @param {object} toEvent
 */
function applyTranslationText(fromEvent, toEvent) {
    toEvent.text = fromEvent.text;
}

/**
 * @param {object} fromEvent
 * @param {object} toEvent
 * @param {Record<string, boolean>} properties
 * @param {object} fromGroup
 * @param {object} toGroup
 */
export function copySelectedProperties(
    fromEvent,
    toEvent,
    properties,
    fromGroup,
    toGroup,
) {
    if (properties.timing) {
        toEvent.start = fromEvent.start;
        toEvent.end = fromEvent.end;
    }

    if (properties.sourceText) {
        applySourceText(fromEvent, toEvent, fromGroup, toGroup);
    }

    if (properties.translationText) {
        applyTranslationText(fromEvent, toEvent);
    }

    if (properties.position) {
        toEvent.xPos = fromEvent.xPos;
        toEvent.yPos = fromEvent.yPos;
        toEvent.alignment = fromEvent.alignment;
    }

    if (properties.speakers) {
        toEvent.speakers = cloneValue(fromEvent.speakers);
    }

    if (properties.style) {
        toEvent.style = fromEvent.style;
        toEvent.forced = fromEvent.forced;
    }

    if (properties.notes) {
        toEvent.notes = fromEvent.notes;
        toEvent.notesCheck = fromEvent.notesCheck;
    }

    if (properties.reply) {
        toEvent.reply = fromEvent.reply;
        toEvent.replyCheck = fromEvent.replyCheck;
        toEvent.approved = fromEvent.approved;
    }

    if (properties.tags) {
        toEvent.tags = cloneValue(fromEvent.tags);
    }

    if (properties.recipients) {
        toEvent.recipients = cloneValue(fromEvent.recipients);
    }
}

/**
 * @param {object} fromEvent
 * @param {object} fromGroup
 * @param {object} toGroup
 * @param {Record<string, boolean>} properties
 * @returns {object}
 */
function createInsertedEvent(fromEvent, fromGroup, toGroup, properties) {
    const clone = JSON.parse(JSON.stringify(fromEvent));
    const fromIsTranslation = isTranslationGroup(fromGroup);
    const toIsTranslation = isTranslationGroup(toGroup);

    if (toIsTranslation && !fromIsTranslation) {
        clone.original = fromEvent.text;
        if (!properties.translationText) {
            clone.text = "";
        }
    } else if (!toIsTranslation && fromIsTranslation) {
        clone.text = fromEvent.original || "";
        clone.original = "";
    }

    copySelectedProperties(fromEvent, clone, properties, fromGroup, toGroup);
    return clone;
}

/**
 * Match destination events by shared id, then by largest time overlap.
 * @param {object[]} fromEvents
 * @param {object[]} toEvents
 * @returns {{ fromIndex: number, toIndex: number }[]}
 */
export function matchEvents(fromEvents, toEvents) {
    const matches = [];
    const usedTo = new Set();

    fromEvents.forEach((fromEvent, fromIndex) => {
        if (!fromEvent?.id) {
            return;
        }

        const toIndex = toEvents.findIndex(
            (toEvent, index) =>
                !usedTo.has(index) && toEvent?.id === fromEvent.id,
        );

        if (toIndex > -1) {
            matches.push({ fromIndex, toIndex });
            usedTo.add(toIndex);
        }
    });

    fromEvents.forEach((fromEvent, fromIndex) => {
        if (matches.some((match) => match.fromIndex === fromIndex)) {
            return;
        }

        let bestToIndex = -1;
        let bestDuration = 0;

        toEvents.forEach((toEvent, toIndex) => {
            if (usedTo.has(toIndex)) {
                return;
            }

            const duration = overlapDuration(fromEvent, toEvent);
            if (duration > bestDuration) {
                bestDuration = duration;
                bestToIndex = toIndex;
            }
        });

        if (bestToIndex > -1) {
            matches.push({ fromIndex, toIndex: bestToIndex });
            usedTo.add(bestToIndex);
        }
    });

    return matches;
}

/**
 * Copy selected properties from one Event Group onto another.
 * Mutates `toGroup.events` in place.
 *
 * @param {object} options
 * @param {object} options.fromGroup
 * @param {object} options.toGroup
 * @param {Record<string, boolean>} [options.properties]
 * @param {boolean} [options.insertMissing]
 * @param {(progress: { current: number, total: number }) => void} [options.onProgress]
 * @returns {{ matched: number, inserted: number, skipped: number }}
 */
export function syncEventGroups({
    fromGroup,
    toGroup,
    properties = DEFAULT_SYNC_PROPERTIES,
    insertMissing = false,
    onProgress,
} = {}) {
    if (!fromGroup || !toGroup) {
        throw new Error("Both Event Groups are required.");
    }

    if (fromGroup.id === toGroup.id) {
        throw new Error("Source and destination Event Groups must be different.");
    }

    const selectedProperties = {
        ...DEFAULT_SYNC_PROPERTIES,
        ...properties,
    };

    const hasSelectedProperty = SYNC_PROPERTY_KEYS.some(
        (key) => selectedProperties[key],
    );

    if (!hasSelectedProperty) {
        throw new Error("Select at least one property to sync.");
    }

    const fromEvents = fromGroup.events || [];
    const toEvents = toGroup.events || [];
    const matches = matchEvents(fromEvents, toEvents);
    const total = Math.max(fromEvents.length, 1);

    matches.forEach((match, index) => {
        copySelectedProperties(
            fromEvents[match.fromIndex],
            toEvents[match.toIndex],
            selectedProperties,
            fromGroup,
            toGroup,
        );

        if (onProgress) {
            onProgress({ current: index + 1, total });
        }
    });

    const unmatchedFromIndexes = fromEvents
        .map((_, index) => index)
        .filter((index) => !matches.some((match) => match.fromIndex === index));

    let inserted = 0;

    if (insertMissing && unmatchedFromIndexes.length > 0) {
        unmatchedFromIndexes.forEach((fromIndex) => {
            const newEvent = createInsertedEvent(
                fromEvents[fromIndex],
                fromGroup,
                toGroup,
                selectedProperties,
            );
            const insertAt = Math.min(fromIndex, toGroup.events.length);
            toGroup.events.splice(insertAt, 0, newEvent);
            inserted += 1;

            if (onProgress) {
                onProgress({
                    current: matches.length + inserted,
                    total,
                });
            }
        });
    }

    const skipped = insertMissing ? 0 : unmatchedFromIndexes.length;

    if (onProgress) {
        onProgress({ current: total, total });
    }

    return {
        matched: matches.length,
        inserted,
        skipped,
    };
}
